import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type PageLayoutStore } from 'shared/layout'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { SurveysApi } from 'entities/Surveys'
import { SurveysAnalyticsApi } from 'entities/Analytics/api/surveysAnalytics'
import { type IParamsAnalyticsSurvey } from 'entities/Analytics/api/types'
import { type IParamsGetSurveys } from 'entities/Surveys/api/types'
import { UsersApi } from 'entities/Users'
import { tableLimitStore } from 'entities/TableLimit'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { SurveyRow } from 'entities/Surveys/model/Table/SurveyRow'
import { SurveySmall } from 'entities/Surveys/model/SurveySmall'
import { ExportAnalyticsModalStore } from 'widgets/ExportAnalyticsModal'
import { type ISurveyType } from 'pages/surveys/config/surveyTypes'
import { SurveyTypeModal } from 'pages/surveys/ui/SurveyTypeModal/SurveyTypeModal'

const MODAL_ID = 'surveysCreateModal'
const SURVEYS_COLUMNS_SOURCE_TYPE = 'surveys-table-columns'

export class SurveysStore {
  isLoading = true
  selectedSurveyType: ISurveyType = 'nps'
  tableStore: TableStore<SurveyRow>
  private _itemsMap: Map<number, SurveyRow> = new Map()
  scrollToTopTrigger = ''
  search = ''
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _hasRetriedAtMinLimit = false
  private readonly _exportModalStore: ExportAnalyticsModalStore<IParamsAnalyticsSurvey>
  private _exportSurveyId: number | null = null

  constructor(readonly entities: IEntitiesStore, readonly pageLayoutStore: PageLayoutStore) {
    this.tableStore = new TableStore<SurveyRow>({
      key: 'surveysTable',
      page: 1,
      limit: 10,
      useColumnsCache: true,
      getLimit: () => tableLimitStore.getLimit('surveys-table'),
      setLimit: (value) => tableLimitStore.setLimit('surveys-table', value),
      saveColumns: this._saveColumns,
      getColumns: this._getColumns,
    })
    this._exportModalStore = new ExportAnalyticsModalStore<IParamsAnalyticsSurvey>((params) => {
      if (this._exportSurveyId === null) {
        return Promise.reject(new Error('Survey for export is not selected'))
      }

      return SurveysAnalyticsApi.exportAnalyticsSurveyById(this._exportSurveyId, params)
    })
    makeAutoObservable(this)
  }

  openExportModal = (id: number) => {
    this._exportSurveyId = id
    this._exportModalStore.onOpenExport({})
  }

  init = () => {
    void this.loadData()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  get empty() {
    return !this.tableStore.loading && !this._itemsMap.size && !this.search
  }

  get cachedLimit() {
    return tableLimitStore.getLimit('surveys-table') ?? 10
  }

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this.tableStore.setLoading(true)
        this.tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  private _saveColumns = async (items: string[]) => {
    try {
      const response = await UsersApi.updateUsersUiSettings({
        source_type: SURVEYS_COLUMNS_SOURCE_TYPE,
        items,
      })

      return response.data.data?.items || []
    } catch (e) {
      logger.error(e)

      return []
    }
  }

  private _getColumns = async (): Promise<string[]> => {
    try {
      const { data } = await UsersApi.getUsersUiSettings(SURVEYS_COLUMNS_SOURCE_TYPE)
      const items = data.data?.items

      return items || []
    } catch (e) {
      logger.error(e)
      return []
    }
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async () => {
      runInAction(() => {
        this.tableStore.setLoading(true)
      })

      try {
        const { data } = await SurveysApi.getSurveys(this.paramsGetItems)

        if (data) {
          runInAction(() => {
            this._hasRetriedAtMinLimit = false
            this._itemsMap.clear()
            data.data.forEach((item) => {
              const survey = new SurveySmall(item)

              this._itemsMap.set(item.id, new SurveyRow(survey))
            })

            this.tableStore.setTotal(data.meta.total)
            this.scrollToTopTrigger = nanoid()
          })
        }
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: surveys-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this.tableStore.limit
          const newLimit = tableLimitStore.reduceLimit('surveys-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
            })
            this.tableStore.setLimit(newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
            })
            this._requestLoadData.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this.isLoading = false
        this.tableStore.setLoading(false)
      })
    },
  })

  get paramsWithoutPage(): Omit<IParamsGetSurveys, 'page'> {
    return {
      limit: this.tableStore.limit,
      'filters[name]': this.search || undefined,
    }
  }

  get paramsGetItems(): IParamsGetSurveys {
    return {
      page: this.tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  setSelectedSurveyType = (type: ISurveyType) => {
    this.selectedSurveyType = type
  }

  setSearch = (search: string) => {
    this.search = search
  }

  openCreateModal = () => {
    modalStore.addModal({
      id: MODAL_ID,
      title: 'Choose a survey type',
      pureContent: true,
      ModalContent: () => <SurveyTypeModal store={this} />,
      width: 798,
    })
  }

  onClose = () => {
    modalStore.removeModal(MODAL_ID)
  }

  onDeleteItem = (id: number) => {
    if (this._itemsMap.size === 1 && this.tableStore.page > 1) {
      this.tableStore.setPage(this.tableStore.page - 1)
    } else {
      this._itemsMap.delete(id)
      this.loadData()
    }
  }

  resetPaginationPage = () => {
    this.tableStore.setPage(1)
    this.tableStore.setLimit(this.cachedLimit)
  }

  dispose = () => {
    this.resetPaginationPage()
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
    this._exportModalStore.dispose()
  }
}
